/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes} checked by tsc
 */
/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import { assertNotNull } from './assert';
import { getOrCreateContainerRef, getOrCreateElementRef, getOrCreateNodeInjectorForNode, getOrCreateTemplateRef } from './di';
import { assertNodeOfPossibleTypes } from './node_assert';
/**
 * A predicate which determines if a given element/directive should be included in the query
 * @record
 */
export function QueryPredicate() { }
function QueryPredicate_tsickle_Closure_declarations() {
    /**
     * Next predicate
     * @type {?}
     */
    QueryPredicate.prototype.next;
    /**
     * Destination to which the value should be added.
     * @type {?}
     */
    QueryPredicate.prototype.list;
    /**
     * If looking for directives than it contains the directive type.
     * @type {?}
     */
    QueryPredicate.prototype.type;
    /**
     * If selector then contains local names to query for.
     * @type {?}
     */
    QueryPredicate.prototype.selector;
    /**
     * Indicates which token should be read from DI for this query.
     * @type {?}
     */
    QueryPredicate.prototype.read;
    /**
     * Values which have been located.
     *
     * this is what builds up the `QueryList._valuesTree`.
     * @type {?}
     */
    QueryPredicate.prototype.values;
}
export class QueryState_ {
    /**
     * @param {?=} deep
     */
    constructor(deep) {
        this.shallow = null;
        this.deep = null;
        this.deep = deep == null ? null : deep;
    }
    /**
     * @template T
     * @param {?} queryList
     * @param {?} predicate
     * @param {?=} descend
     * @param {?=} read
     * @return {?}
     */
    track(queryList, predicate, descend, read) {
        // TODO(misko): This is not right. In case of inherited state, a calling track will incorrectly
        // mutate parent.
        if (descend) {
            this.deep = createPredicate(this.deep, queryList, predicate, read != null ? read : null);
        }
        else {
            this.shallow =
                createPredicate(this.shallow, queryList, predicate, read != null ? read : null);
        }
    }
    /**
     * @return {?}
     */
    child() {
        if (this.deep === null) {
            // if we don't have any deep queries than no need to track anything more.
            return null;
        }
        if (this.shallow === null) {
            // DeepQuery: We can reuse the current state if the child state would be same as current
            // state.
            return this;
        }
        else {
            // We need to create new state
            return new QueryState_(this.deep);
        }
    }
    /**
     * @param {?} node
     * @return {?}
     */
    addNode(node) {
        add(this.shallow, node);
        add(this.deep, node);
    }
    /**
     * @param {?} container
     * @param {?} view
     * @param {?} index
     * @return {?}
     */
    insertView(container, view, index) {
        throw new Error('Method not implemented.');
    }
    /**
     * @param {?} container
     * @param {?} view
     * @param {?} index
     * @return {?}
     */
    removeView(container, view, index) {
        throw new Error('Method not implemented.');
    }
}
function QueryState__tsickle_Closure_declarations() {
    /** @type {?} */
    QueryState_.prototype.shallow;
    /** @type {?} */
    QueryState_.prototype.deep;
}
/**
 * Iterates over local names for a given node and returns directive index
 * (or -1 if a local name points to an element).
 *
 * @param {?} staticData static data of a node to check
 * @param {?} selector selector to match
 * @return {?} directive index, -1 or null if a selector didn't match any of the local names
 */
function getIdxOfMatchingSelector(staticData, selector) {
    const /** @type {?} */ localNames = staticData.localNames;
    if (localNames) {
        for (let /** @type {?} */ i = 0; i < localNames.length; i += 2) {
            if (localNames[i] === selector) {
                return /** @type {?} */ (localNames[i + 1]);
            }
        }
    }
    return null;
}
/**
 * Iterates over all the directives for a node and returns index of a directive for a given type.
 *
 * @param {?} node Node on which directives are present.
 * @param {?} type Type of a directive to look for.
 * @return {?} Index of a found directive or null when none found.
 */
function geIdxOfMatchingDirective(node, type) {
    const /** @type {?} */ ngStaticData = node.view.ngStaticData;
    const /** @type {?} */ flags = node.flags;
    for (let /** @type {?} */ i = flags >> 12 /* INDX_SHIFT */, /** @type {?} */
    ii = i + ((flags & 4092 /* SIZE_MASK */) >> 2 /* SIZE_SHIFT */); i < ii; i++) {
        const /** @type {?} */ def = /** @type {?} */ (ngStaticData[i]);
        if (def.diPublic && def.type === type) {
            return i;
        }
    }
    return null;
}
/**
 * @param {?} nodeInjector
 * @param {?} node
 * @return {?}
 */
function readDefaultInjectable(nodeInjector, node) {
    ngDevMode && assertNodeOfPossibleTypes(node, 0 /* Container */, 3 /* Element */);
    if ((node.flags & 3 /* TYPE_MASK */) === 3 /* Element */) {
        return getOrCreateElementRef(nodeInjector);
    }
    else if ((node.flags & 3 /* TYPE_MASK */) === 0 /* Container */) {
        return getOrCreateTemplateRef(nodeInjector);
    }
}
/**
 * @param {?} nodeInjector
 * @param {?} node
 * @param {?} read
 * @return {?}
 */
function readFromNodeInjector(nodeInjector, node, read) {
    if (read === 0 /* ElementRef */) {
        return getOrCreateElementRef(nodeInjector);
    }
    else if (read === 1 /* ViewContainerRef */) {
        return getOrCreateContainerRef(nodeInjector);
    }
    else if (read === 2 /* TemplateRef */) {
        return getOrCreateTemplateRef(nodeInjector);
    }
    else {
        const /** @type {?} */ matchingIdx = geIdxOfMatchingDirective(node, read);
        if (matchingIdx !== null) {
            return node.view.data[matchingIdx];
        }
    }
    return null;
}
/**
 * @param {?} predicate
 * @param {?} node
 * @return {?}
 */
function add(predicate, node) {
    const /** @type {?} */ nodeInjector = getOrCreateNodeInjectorForNode(/** @type {?} */ (node));
    while (predicate) {
        const /** @type {?} */ type = predicate.type;
        if (type) {
            const /** @type {?} */ directiveIdx = geIdxOfMatchingDirective(node, type);
            if (directiveIdx !== null) {
                if (predicate.read !== null) {
                    const /** @type {?} */ requestedRead = readFromNodeInjector(nodeInjector, node, predicate.read);
                    if (requestedRead !== null) {
                        predicate.values.push(requestedRead);
                    }
                }
                else {
                    predicate.values.push(node.view.data[directiveIdx]);
                }
            }
        }
        else {
            const /** @type {?} */ selector = /** @type {?} */ ((predicate.selector));
            for (let /** @type {?} */ i = 0; i < selector.length; i++) {
                ngDevMode && assertNotNull(node.staticData, 'node.staticData');
                const /** @type {?} */ directiveIdx = getIdxOfMatchingSelector(/** @type {?} */ ((node.staticData)), selector[i]);
                // is anything on a node matching a selector?
                if (directiveIdx !== null) {
                    if (predicate.read !== null) {
                        const /** @type {?} */ requestedRead = readFromNodeInjector(nodeInjector, node, predicate.read);
                        if (requestedRead !== null) {
                            predicate.values.push(requestedRead);
                        }
                    }
                    else {
                        // is local name pointing to a directive?
                        if (directiveIdx > -1) {
                            predicate.values.push(node.view.data[directiveIdx]);
                        }
                        else {
                            predicate.values.push(readDefaultInjectable(nodeInjector, node));
                        }
                    }
                }
            }
        }
        predicate = predicate.next;
    }
}
/**
 * @template T
 * @param {?} previous
 * @param {?} queryList
 * @param {?} predicate
 * @param {?} read
 * @return {?}
 */
function createPredicate(previous, queryList, predicate, read) {
    const /** @type {?} */ isArray = Array.isArray(predicate);
    const /** @type {?} */ values = /** @type {?} */ ([]);
    if ((/** @type {?} */ ((queryList)))._valuesTree === null) {
        (/** @type {?} */ ((queryList)))._valuesTree = values;
    }
    return {
        next: previous,
        list: queryList,
        type: isArray ? null : /** @type {?} */ (predicate),
        selector: isArray ? /** @type {?} */ (predicate) : null,
        read: read,
        values: values
    };
}
class QueryList_ {
    constructor() {
        this.dirty = false;
        /**
         * \@internal
         */
        this._valuesTree = null;
        /**
         * \@internal
         */
        this._values = null;
    }
    /**
     * @return {?}
     */
    get length() {
        ngDevMode && assertNotNull(this._values, 'refreshed');
        return /** @type {?} */ ((this._values)).length;
    }
    /**
     * @return {?}
     */
    get first() {
        ngDevMode && assertNotNull(this._values, 'refreshed');
        let /** @type {?} */ values = /** @type {?} */ ((this._values));
        return values.length ? values[0] : null;
    }
    /**
     * @return {?}
     */
    get last() {
        ngDevMode && assertNotNull(this._values, 'refreshed');
        let /** @type {?} */ values = /** @type {?} */ ((this._values));
        return values.length ? values[values.length - 1] : null;
    }
    /**
     * \@internal
     * @return {?}
     */
    _refresh() {
        // TODO(misko): needs more logic to flatten tree.
        if (this._values === null) {
            this._values = this._valuesTree;
            return true;
        }
        return false;
    }
    /**
     * @template U
     * @param {?} fn
     * @return {?}
     */
    map(fn) {
        throw new Error('Method not implemented.');
    }
    /**
     * @param {?} fn
     * @return {?}
     */
    filter(fn) {
        throw new Error('Method not implemented.');
    }
    /**
     * @param {?} fn
     * @return {?}
     */
    find(fn) {
        throw new Error('Method not implemented.');
    }
    /**
     * @template U
     * @param {?} fn
     * @param {?} init
     * @return {?}
     */
    reduce(fn, init) {
        throw new Error('Method not implemented.');
    }
    /**
     * @param {?} fn
     * @return {?}
     */
    forEach(fn) {
        throw new Error('Method not implemented.');
    }
    /**
     * @param {?} fn
     * @return {?}
     */
    some(fn) {
        throw new Error('Method not implemented.');
    }
    /**
     * @return {?}
     */
    toArray() {
        ngDevMode && assertNotNull(this._values, 'refreshed');
        return /** @type {?} */ ((this._values));
    }
    /**
     * @return {?}
     */
    toString() { throw new Error('Method not implemented.'); }
    /**
     * @param {?} res
     * @return {?}
     */
    reset(res) { throw new Error('Method not implemented.'); }
    /**
     * @return {?}
     */
    notifyOnChanges() { throw new Error('Method not implemented.'); }
    /**
     * @return {?}
     */
    setDirty() { throw new Error('Method not implemented.'); }
    /**
     * @return {?}
     */
    destroy() { throw new Error('Method not implemented.'); }
}
function QueryList__tsickle_Closure_declarations() {
    /** @type {?} */
    QueryList_.prototype.dirty;
    /** @type {?} */
    QueryList_.prototype.changes;
    /**
     * \@internal
     * @type {?}
     */
    QueryList_.prototype._valuesTree;
    /**
     * \@internal
     * @type {?}
     */
    QueryList_.prototype._values;
}
export const /** @type {?} */ QueryList = /** @type {?} */ (QueryList_);
/**
 * @param {?} query
 * @return {?}
 */
export function queryRefresh(query) {
    return (/** @type {?} */ ((query)))._refresh();
}
//# sourceMappingURL=query.js.map